import { createSlice } from '@reduxjs/toolkit';
import { request } from '@/utils';

const parseProjectCreateTime = (item) => {
    const raw = item?.createDate ?? item?.createTime ?? item?.createdAt ?? '';
    if (!raw && raw !== 0) return 0;

    const normalizeTimestamp = (value) => (value > 0 && value < 1e12 ? value * 1000 : value);

    if (typeof raw === 'number') {
        return Number.isFinite(raw) ? normalizeTimestamp(raw) : 0;
    }

    if (typeof raw === 'string') {
        const normalized = raw.trim();
        if (!normalized) return 0;

        if (/^\d+$/.test(normalized)) {
            const numeric = Number(normalized);
            return Number.isFinite(numeric) ? normalizeTimestamp(numeric) : 0;
        }

        // 后端常返回 "YYYY-MM-DD HH:mm:ss"，替换为空间兼容浏览器 Date 解析。
        const timestamp = Date.parse(normalized.replace(' ', 'T'));
        return Number.isNaN(timestamp) ? 0 : timestamp;
    }

    return 0;
};

const sortProjectsByNewest = (projectInfos = []) => {
    return [...projectInfos].sort((a, b) => {
        const timeDiff = parseProjectCreateTime(b) - parseProjectCreateTime(a);
        if (timeDiff !== 0) return timeDiff;

        // 同创建时间时按 projectId 倒序兜底，尽量保证新项目靠前。
        const aId = String(a?.projectId ?? '');
        const bId = String(b?.projectId ?? '');
        return bId.localeCompare(aId, 'zh-CN', { numeric: true, sensitivity: 'base' });
    });
};

// 初始状态
const initialState = {
    projectList: [],
    pagination: {
        current: 1,
        pageSize: 10,
        total: 0
    },
    loading: false,
    currentProjectId: null
};

// 创建 slice
const projectSlice = createSlice({
    name: 'project',
    initialState,
    reducers: {
        setProjectList: (state, action) => {
            state.projectList = action.payload;
        },
        setPagination: (state, action) => {
            state.pagination = action.payload;
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setCurrentProjectId: (state, action) => {
            state.currentProjectId = action.payload;
        }
    }
});

// 导出 actions
export const { setProjectList, setPagination, setLoading, setCurrentProjectId } = projectSlice.actions;

// 添加一个选择器以便于获取 currentProjectId
export const selectCurrentProjectId = (state) => state.project.currentProjectId;

// 异步 action - 使用真实接口
export const fetchProjectList = ({ pageNum, pageSize, projectStatusFlag, keyword, projectBusinessType }) => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        const response = await request('/project/queryProjectList', {
            method: 'GET',
            params: {
                pageNum,
                pageSize,
                projectStatusFlag,
                keyword,
                projectBusinessType,
            }
        });

        const projectInfos = response?.data?.projectBriefInfos || [];
        const sortedProjectInfos = sortProjectsByNewest(projectInfos);
        const projectList = sortedProjectInfos.map(item => ({
            key: item.projectId,
            projectId: item.projectId,
            materialType: item.projectName,
            materialName: item.createDate || item.createTime || item.createdAt || '暂无日期',
            unitName: item.managerName,
            partnerName: item.partnerName || '-',
            tenantUnit: item.tenantUnit || '-',
            projectBusinessType: item.projectBusinessType,
        }));

        dispatch(setProjectList(projectList));
        dispatch(setPagination({
            current: response?.data?.current ?? pageNum ?? 1,
            pageSize,
            total: response?.data?.totalNum ?? projectList.length
        }));
    } catch (error) {
        console.error('获取项目列表失败:', error);
        // 失败时清空旧数据，避免界面保留上一次查询结果造成“切换无效”错觉
        dispatch(setProjectList([]));
        dispatch(setPagination({
            current: pageNum ?? 1,
            pageSize: pageSize ?? 10,
            total: 0
        }));
    } finally {
        dispatch(setLoading(false));
    }
};

// 删除项目
export const deleteProject = (projectId) => async (dispatch) => {
    try {
        const response = await request({
            url: '/project/deleteProject',
            method: 'GET',
            params: { projectId }
        });
        return response;
    } catch (error) {
        console.error('删除项目失败:', error);
        return false;
    }
};

// 创建项目
export const createProject = (projectData) => async (dispatch) => {
    try {
        const response = await request({
            url: '/project/createProject',
            method: 'POST',
            data: projectData
        });

        // 检查 succeed 字段
        if (response.succeed) {
            // 创建成功后重新获取列表
            dispatch(fetchProjectList({
                pageNum: 1,
                pageSize: 10,
                projectStatusFlag: '0',  // 新建的项目默认在进行中列表
                projectBusinessType: projectData.projectBusinessType,
            }));
            return response;  // 返回完整的响应数据
        } else {
            console.error('创建项目失败:', response);
            return response;
        }
    } catch (error) {
        console.error('创建项目请求失败:', error);
        return error?.response?.data || {
            succeed: false,
            errorMessage: error?.message || '创建项目失败'
        };
    }
};

export default projectSlice.reducer; 
